/**
 * Pure math for the homepage "How it works" scroll sequence. `stageProgress` runs from 0 to
 * `stepCount` (4): the integer part is the current step, the fraction is progress within it.
 */

export const FIRST_PROCESS_SRC = '/images/first-process.webp';
export const CENTER_IMAGE_SRC = '/images/center1.webp';
export const WATCH_GROW_SRC = '/images/watch_grow.svg';
export const WITHDRAW_RET_SRC = '/images/withdraw_ret.webp';

export function imageForStage(stageIndex: number): string {
  switch (stageIndex) {
    case 0:
      return FIRST_PROCESS_SRC;
    case 1:
      return CENTER_IMAGE_SRC;
    case 2:
      return WATCH_GROW_SRC;
    case 3:
      return WITHDRAW_RET_SRC;
    default:
      return CENTER_IMAGE_SRC;
  }
}

// Logical column for each step (rows) at each stage (cols).
// On-stage cells match the static `columnSteps` layout; off-stage cells extend
// linearly so the step slides into / out of view at the edges instead of
// appearing or disappearing abruptly.
const STEP_COLUMNS_PER_STAGE: number[][] = [
  [1, 0, -1, -2],
  [2, 2, 0, -1],
  [3, 3, 2, 0],
  [4, 4, 3, 2],
];

export function stepColAt(stepIndex: number, stageIndex: number): number {
  const row = STEP_COLUMNS_PER_STAGE[stepIndex];
  if (!row) return 0;
  const clamped = Math.max(0, Math.min(row.length - 1, stageIndex));
  return row[clamped];
}

export function imageColForStage(stageIndex: number): number {
  return stageIndex === 0 ? 0 : 1;
}

export function navColForStage(stageIndex: number, maxStage: number): number {
  if (stageIndex === 0) return 0;
  if (stageIndex >= maxStage) return 2;
  return 1;
}

export function smoothstep(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}

const LOADER_FADE_BAND = 0.05;

export function progressForStage(stageProgress: number, stageIndex: number): number {
  return Math.max(0, Math.min(1, stageProgress - stageIndex));
}

export function loaderFillPctFromProgress(sp: number): number {
  return Math.round(Math.max(0, Math.min(1, sp)) * 100);
}

function loaderFadeInOpacity(sp: number): number {
  if (sp >= LOADER_FADE_BAND) return 1;
  return sp / LOADER_FADE_BAND;
}

function loaderFadeOutOpacity(sp: number): number {
  if (sp <= 1 - LOADER_FADE_BAND) return 1;
  return (1 - sp) / LOADER_FADE_BAND;
}

function loaderOpacity(sp: number): number {
  return loaderFadeInOpacity(sp) * loaderFadeOutOpacity(sp);
}

// Section 2 → 3 (Buy a stake → Watch it grow) crossfades in place at the center column.
export const IN_PLACE_FROM_STAGE = 1;
const IN_PLACE_CROSSFADE_START = IN_PLACE_FROM_STAGE + 1 - LOADER_FADE_BAND;
const IN_PLACE_CROSSFADE_END = IN_PLACE_FROM_STAGE + 1 + LOADER_FADE_BAND;

export function inPlaceCrossfadeOpacities(
  stageProgress: number,
): { outgoing: number; incoming: number } | null {
  if (stageProgress < IN_PLACE_CROSSFADE_START || stageProgress > IN_PLACE_CROSSFADE_END) {
    return null;
  }
  const t =
    (stageProgress - IN_PLACE_CROSSFADE_START) /
    (IN_PLACE_CROSSFADE_END - IN_PLACE_CROSSFADE_START);
  return { outgoing: 1 - t, incoming: t };
}

export function navStageOpacity(
  stageProgress: number,
  stageIndex: number,
  stepCount: number,
): number {
  const lastStageIndex = stepCount - 1;
  const crossfade = inPlaceCrossfadeOpacities(stageProgress);
  if (crossfade) {
    if (stageIndex === IN_PLACE_FROM_STAGE) return crossfade.outgoing;
    if (stageIndex === IN_PLACE_FROM_STAGE + 1) return crossfade.incoming;
  }
  const sp = progressForStage(stageProgress, stageIndex);
  // First section starts active at page entry — no fade-in from zero.
  if (stageIndex === 0 && stageProgress < LOADER_FADE_BAND) {
    return loaderFadeOutOpacity(sp);
  }
  // Last section holds through release — no fade-out to white.
  if (stageIndex === lastStageIndex && stageProgress >= lastStageIndex) {
    return 1;
  }
  return loaderOpacity(sp);
}

export type FlipState = {
  /** Whole steps completed, clamped to [0, maxStage]. */
  baseStage: number;
  /** 0..1 eased progress through the flip that leads into the next step. */
  flipT: number;
  /** Total card rotation in degrees (each step is another half turn). */
  flipDeg: number;
};

/** Scroll position → card flip. The flip starts late in a step and finishes exactly at the boundary. */
export function flipForProgress(stageProgress: number, maxStage: number): FlipState {
  const baseStage = Math.max(0, Math.min(maxStage, Math.floor(stageProgress)));
  let flipT = 0;
  if (baseStage < maxStage) {
    const within = stageProgress - baseStage;
    const start = baseStage === 1 ? 0.55 : 0.75;
    flipT = smoothstep((within - start) / (1 - start));
  }
  return { baseStage, flipT, flipDeg: baseStage * 180 + flipT * 180 };
}

/**
 * Which image each face of the card should show.
 *
 * At rest on step k the card is rotated k half-turns: even steps show the FRONT face, odd steps the
 * BACK face. The face that is currently hidden must already hold the NEXT step's image, so when the
 * card turns, the new image is what rotates into view. (Deriving the faces from the *current* step
 * instead — the old behaviour — showed the previous image on the turning face and swapped it only
 * after the step changed, which is the flicker.)
 */
export function flipFaceSources(baseStage: number, maxStage: number): { front: string; back: string } {
  const next = Math.min(baseStage + 1, maxStage);
  const frontStage = baseStage % 2 === 0 ? baseStage : next;
  const backStage = baseStage % 2 === 1 ? baseStage : next;
  return { front: imageForStage(frontStage), back: imageForStage(backStage) };
}
